
export enum UserRole {
  DATA_AGENT = 'DATA_AGENT',
  RESEARCHER = 'RESEARCHER',
  GENERAL_USER = 'GENERAL_USER',
  CTO = 'CTO',
  AI_AGENT = 'AI_AGENT'
}

export interface Campaign {
  id: string;
  title: string;
  description: string;
  reward: number;
}

export interface Transaction {
  id: string;
  type: 'EARN' | 'REDEEM';
  amount: number;
  timestamp: string;
  hash: string;
}

export interface DataCategory {
  id: number;
  name: string;
  name_kh?: string;
  keywords?: string[];
}

export type PeriodGranularity = 'YEAR' | 'QUARTER' | 'MONTH' | 'DAY' | 'POINT';

// The "metric" dimension that turns data points into queryable time series.
export interface Indicator {
  id: string;            // slug, e.g. 'rice_yield'
  name_en: string;
  name_kh?: string;
  category_id?: number;
  canonical_unit?: string;
  value_type?: 'NUMERIC' | 'TEXT';
  definition?: string;
}

export interface DataSource {
  id: string;
  name: string;
  type: string;
  website_url?: string;
  credibility: number;
  created_at: string;
  created_by?: string;
}

// Used for the extraction workbench (temporary state)
export interface DraftDataPoint {
  id: string;
  category: string;
  location: string;
  date_reported: string;
  value_numeric?: number;
  value_text?: string;
  unit: string;
  tags?: string[];
  summary_text: string;
  confidence_warning?: boolean;
  // Statistical-DB fields (optional in drafts/imports)
  indicator?: string;          // indicator slug
  period_start?: string;
  period_end?: string;
  period_granularity?: PeriodGranularity;
  published_at?: string;       // when the source published the figure
  confidence?: number;
}

// Used for the actual DB table 'public_data_points'
export interface PublicDataPoint {
  id: string;
  source_id: string; // Corrected from data_source_id
  category_id: number;
  location_id?: number;
  date_reported: string;
  value_numeric?: number; // Now nullable
  value_text?: string;    // New field
  unit: string;
  tags?: string[];        // New field
  summary_text: string;
  evidence_file_path?: string;
  created_at: string;
  verified_by_user_id?: string;

  // Statistical-DB dimensions
  indicator_id?: string | null;
  period_start?: string | null;
  period_end?: string | null;
  period_granularity?: PeriodGranularity | null;
  published_at?: string | null;
  confidence?: number | null;
  version?: number;
  superseded_by?: string | null;

  // Joins
  source?: DataSource;
  category?: DataCategory;
  indicator?: Indicator;
}

// --- SURVEY SYSTEM TYPES ---

export type SurveyStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'COMPLETED';
export type RewardType = 'DIGITAL' | 'PHYSICAL' | 'NONE';

export type QuestionType = 'TEXT' | 'MULTIPLE_CHOICE' | 'CHECKBOX' | 'RATING' | 'DATE' | 'MATRIX' | 'GEO_LOCATION' | 'IMAGE_UPLOAD' | 'RANKING';

export interface LocalizedText {
  en?: string;
  [key: string]: string | undefined;
}

export interface SurveyOption {
  id: string;
  label: LocalizedText;
  next_question_index?: number | null; // For simple skip logic (index based)
  action?: 'SUBMIT' | 'NEXT';
}

export interface QuestionConfig {
  options?: SurveyOption[];
  rows?: SurveyOption[];    // For Matrix
  columns?: SurveyOption[]; // For Matrix
  required?: boolean;
  min?: number;
  max?: number;
  step?: number;
  next_question_index?: number; // New: For Question-level skip logic (default jump)
  labels?: { [key: string]: string }; // e.g. "1": "Bad", "5": "Good"
}

export interface SurveyQuestionDraft {
  id: string; // Temp ID for UI
  order_index: number;
  question_type: QuestionType;
  question_text: LocalizedText;
  config: QuestionConfig;
  gemini_validation_prompt?: string;
  // Optional link to a vault attribute (e.g. 'age', 'gender'): pre-fill from the
  // respondent's encrypted vault and offer to save their answer back to it.
  attribute_id?: string | null;
}

// --- REWARD / GIFTING SYSTEM ---

export interface DigitalReward {
  id: string;
  name: string;
  amount: number;
  currency: string;
  contract_address?: string;
  chain?: string; // e.g., 'BSC', 'POLYGON'
  created_at?: string;
}

export interface LootItem {
  id: string;
  name: string;
  type: 'TOKEN' | 'NFT' | 'PHYSICAL' | 'VOUCHER';
  value: number; // Estimated value or ID ref
  probability: number; // 0-100%
  total_qty: number;     // Initial stock
  remaining_qty: number; // Current stock (decrements on win)
  image_url?: string;
}

export interface LootTable {
  id: string;
  name: string;
  description?: string;
  items: LootItem[]; // Fetched via join
  created_at: string;
  is_mystery?: boolean;   // openable by spending points
  point_cost?: number;    // points to open
  is_community?: boolean; // the single global box sponsored by survey owners
}

export type SponsorshipStatus = 'PENDING_PAYMENT' | 'PAID' | 'CANCELLED';

// A survey owner funds a prize into the global Community Mystery Box.
export interface MysterySponsorship {
  id: string;
  survey_id: string | null;
  sponsor_id: string;
  reward_name: string;
  amount: number;
  currency: string;
  status: SponsorshipStatus;
  payment_method: string;
  payment_ref: string | null;
  loot_table_id: string | null;
  loot_item_id: string | null;
  created_at: string;
  paid_at: string | null;
}

export interface Survey {
  id: string;
  title: LocalizedText;
  description: LocalizedText;
  status: SurveyStatus;
  max_responses: number | null; // Null for unlimited
  created_at: string;
  creator_id?: string;
  
  // POLYMORPHIC REWARD LINK
  reward_type: RewardType;
  
  // Specific FKs are needed for PostgREST joins to work correctly
  digital_reward_id?: string | null;
  loot_table_id?: string | null;
  
  is_kiosk?: boolean; // New flag for touch-only mode
  is_live?: boolean; // Live mode: present results on a big screen, audience votes by QR
  completion_box?: boolean; // Members open a mystery box (owner's loot table) on completion
  require_login?: boolean; // New flag for Access Control

  // Provenance: 'NATIVE' = collected in Tinaney, 'IMPORT_CSV' = imported from a
  // spreadsheet/CSV export. Imported data earns no rewards and is labelled lower-trust.
  source?: 'NATIVE' | 'IMPORT_CSV';

  // DASHBOARD RELEASE: publish this survey's analytics dashboard (costs credits).
  // Released FREE (any signed-in user) or PAID (KHQR/x402, paid directly to the
  // researcher), as the interactive VISUAL dashboard and/or a JSON export.
  dashboard_published?: boolean;
  dashboard_access?: 'FREE' | 'PAID';
  dashboard_formats?: ('VISUAL' | 'JSON')[];
  dashboard_for_sale?: boolean;
  dashboard_price?: number | null;
  dashboard_currency?: string;
  dashboard_pay_methods?: ('KHQR' | 'X402')[];

  // DATA SHARING: attribute definition ids the researcher requests from the
  // respondent's encrypted vault, plus reputation points awarded for consenting.
  requested_attribute_ids?: string[] | null;
  data_reward_points?: number | null;
  // Creator-tunable points awarded for answering (default 1). Owners raise it to compete.
  completion_reward_points?: number | null;
  // Optional audience targeting (coarse value_summary tokens). When set, only
  // respondents matching ALL filters earn the data reward.
  target_attributes?: { attribute_id: string; value: string }[] | null;

  // Hydrated objects (joins)
  digital_reward?: DigitalReward;
  loot_table?: LootTable;
}

export interface SurveyAnswer {
  id: string;
  response_id: string;
  question_id: string;
  answer_value: any;
  question?: {
    question_text: LocalizedText;
    question_type: QuestionType;
    order_index?: number;
    config?: QuestionConfig;
  };
}

export interface SurveyResponse {
  id: string;
  survey_id: string;
  user_id?: string;
  status: string;
  completed_at: string;
  user?: {
    email?: string;
    full_name?: string;
  };
  answers?: SurveyAnswer[];
}

export interface RewardLog {
  id: string;
  user_id: string;
  action_type: string; // 'DIGITAL_REWARD', 'LOOT_BOX'
  
  // Common Fields
  status: 'AVAILABLE' | 'OPENED' | 'CLAIMED' | 'SHIPPED';
  created_at: string;

  // Digital Specific
  amount?: number;
  currency?: string;
  wallet_address?: string;
  tx_hash?: string;

  // Physical/Loot Specific
  loot_table_id?: string;
  item_name?: string;
  item_image?: string;
  shipping_info?: string;
}
